import dayjs from "dayjs";

import React, { forwardRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SentimentDissatisfiedRoundedIcon from "@mui/icons-material/SentimentDissatisfiedRounded";
import { Button, Dialog, DialogContent, Grid, Slide, Typography } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { PRO_ALERT_LIMIT } from "@/constants/membership";
import { AlertTypeEnum } from "@/constants";
import { AirportMapItem } from "@/constants/airport";
import useEvent from "@/hooks/useEvent";
import usePlatform from "@/hooks/usePlatform";

import { LocationOption } from "../hooks/useLocationFilter";

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export interface AlertResultProps {
  open: boolean;
  onOpen: (value: boolean) => void;
  onNewAlert: () => void;
  data?: {
    alertType?: AlertTypeEnum;
    fromLocation?: Array<AirportMapItem | LocationOption | string>;
    toLocation?: Array<AirportMapItem | LocationOption | string>;
    departureDate?: number;
    departureDateSec?: number;
    isSuccess?: boolean;
    alertLimit?: number;
  };
}

const AlertResult = ({ data = {}, open, onOpen, onNewAlert }: AlertResultProps) => {
  const { isMobile } = usePlatform();
  const navigator = useNavigate();
  const location = useLocation();

  const handleClose = useEvent(() => {
    onOpen(false);
  });

  const manageAlert = useEvent(() => {
    const path = "/alert";

    if (location.pathname === path) {
      handleClose();
    } else {
      handleClose();
      navigator(path);
    }
  });

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      sx={{
        maxWidth: "430px",
        margin: "auto",
        "& .MuiDialog-paper": {
          margin: 0,
          borderRadius: "12px",
          width: "100%",
          maxWidth: "calc(100% - 32px)",
        },
        "& .MuiDialogContent-root": {
          padding: isMobile ? "20px" : "20px 24px",
        },
      }}
      onClose={handleClose}
    >
      <DialogContent>
        <div className="flex flex-col w-full items-center justify-center py-[10px]">
          {data.isSuccess ? (
            <>
              <CheckCircleOutlineIcon
                className="!text-[50px] mb-[10px]"
                sx={{ color: (theme) => theme.palette.success.light }}
              />

              <Typography variant="body1" className="!text-[20px] pb-[10px]">
                Tracking is ongoing!
              </Typography>


              {data.alertType === AlertTypeEnum.Hotel ? (
                <Typography variant="body2" color="textSecondary" className="pb-[5px]">
                  {(data.fromLocation || []).join(",")}
                </Typography>
              ) : (
                <Typography variant="body2" color="textSecondary" className="pb-[5px]">
                  {(data.fromLocation || [])
                    .map((ele) => {
                      if (data.alertType === AlertTypeEnum.Flight) {
                        return (ele as AirportMapItem).iata;
                      } else if (data.alertType === AlertTypeEnum.Panorama) {
                        return (ele as LocationOption).name;
                      } else {
                        return ele;
                      }
                    })
                    .join(",")}
                  {` - `}
                  {(data.toLocation || [])
                    .map((ele) => {
                      if (data.alertType === AlertTypeEnum.Flight) {
                        return (ele as AirportMapItem).iata;
                      } else if (data.alertType === AlertTypeEnum.Panorama) {
                        return (ele as LocationOption).name;
                      } else {
                        return ele;
                      }
                    })
                    .join(",")}
                </Typography>
              )}

              <Typography variant="body2" color="textSecondary">
                {dayjs(data.departureDate).format("ddd, DD MMM YYYY")}
                {` - `}
                {dayjs(data.departureDateSec).format("ddd, DD MMM YYYY")}
              </Typography>
              <Typography variant="body1" className="pt-[5px]" >
                We've sent a confirmation email to your inbox. If you don't see it, please check your spam or junk folder, and be sure to <a href="https://awardx.helpscoutdocs.com/article/8-how-to-add-our-email-to-your-whitelist" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>add our email to your whitelist</a> to ensure future delivery.
              </Typography>

              <Button variant="text" className="!mt-[5px]" onClick={manageAlert}>
                Manage Alerts
              </Button>

              <Grid
                container
                className="!mt-[16px]"
                columnSpacing={2}
                rowGap={2}
                direction={isMobile ? "column-reverse" : "row"}
              >
                <Grid item xs={isMobile ? 12 : 6}>
                  <Button variant="outlined" fullWidth onClick={handleClose}>
                    OK
                  </Button>
                </Grid>
                <Grid item xs={isMobile ? 12 : 6}>
                  <Button variant="contained" fullWidth onClick={onNewAlert}>
                    Create New Alert
                  </Button>
                </Grid>
              </Grid>
            </>
          ) : (
            <>
              <SentimentDissatisfiedRoundedIcon
                className="!text-[50px] mb-[10px]"
                sx={{ color: (theme) => theme.palette.error.dark }}
              />

              <Typography variant="body1" className="!text-[20px] pb-[10px]">
                Oops, all {data.alertLimit} alerts created
              </Typography>

              <Typography variant="body2" color="textSecondary" className="pb-[5px]">
                PRO user gets {PRO_ALERT_LIMIT} active award award alerts.
              </Typography>

              <Button variant="text" className="!mt-[10px]" onClick={manageAlert}>
                Manage Alerts
              </Button>

              <Grid
                container
                className="!mt-[16px]"
                columnSpacing={2}
                rowGap={2}
                direction={isMobile ? "column-reverse" : "row"}
              >
                <Grid item xs={isMobile ? 12 : 12}>
                  <Button variant="outlined" fullWidth onClick={handleClose}>
                    Cancel
                  </Button>
                </Grid>
                {/* <Grid item xs={isMobile ? 12 : 12}>
                  <Button variant="contained" fullWidth onClick={handleClose}>
                    Get Unlimited Alerts
                  </Button>
                </Grid> */}
              </Grid>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(AlertResult);
